import {
  Box,
  IconButton,
  Paper,
} from "@mui/material";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import RowActionButtons from "../../../../components/table/RowActionButtons";
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
} from "@mui/x-data-grid";
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import FilterPanel from "../../../../components/FilterPanel";
import FilterSearchField from "../../../../components/FilterPanel/FilterSearchField";
import { sourceSystemService } from "../../../../services/sourceSystemService";
import { extractTableFieldsFromColumns } from "../../../../lib/api/readQuery";
import { getCurrentUserRoles, isViewer } from "../../../../utils/authorization";
import { notifySuccessShort } from "../../../../utils/notification";
import { rdtLikeToDataGridColumns, type RdtLikeColumn } from "../../../../lib/ui/rdtLikeToDataGridColumns";
import { fillDataGridSx, fillDataTableGridWrapSx, fillDataTableRootSx } from "../../../../lib/ui/pageLayout";

type PagedResponse = {
  content?: Record<string, unknown>[];
  page?: { totalElements?: number };
};

const isSourceSystemRecord = (row: unknown): row is Record<string, unknown> => {
  if (!row || typeof row !== "object") {
    return false;
  }

  const record = row as Record<string, unknown>;
  const hasCustomerFields = Boolean(
    record.username ||
      (Array.isArray(record.users) && record.users.length > 0) ||
      record.email ||
      record.phone ||
      record.mobilePhone ||
      record.pointOfContact,
  );
  const hasSourceSystemFields =
    Boolean(record.customerId) || Array.isArray(record.contacts) || Array.isArray(record.sources);

  if (hasCustomerFields && !hasSourceSystemFields) {
    return false;
  }

  return hasSourceSystemFields || !hasCustomerFields;
};

const normalizePagedSourceSystems = (response: PagedResponse): PagedResponse => {
  const content = Array.isArray(response.content)
    ? response.content.filter(isSourceSystemRecord)
    : [];

  return {
    content,
    page: response.page,
  };
};

type ProfileSourceSystemsTableProps = {
  customerId: string;
  cols: RdtLikeColumn[];
  handleEdit?: (row: Record<string, unknown>) => void | Promise<void>;
  handleDelete?: (row: Record<string, unknown>) => void | Promise<void>;
  headerActions?: React.ReactNode;
};

const ProfileSourceSystemsTable = React.forwardRef(
  ({ customerId, cols, handleEdit, handleDelete, headerActions }: ProfileSourceSystemsTableProps, ref) => {
    const { t } = useTranslation();
    const [data, setData] = useState<PagedResponse>({ content: [], page: {} });
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(50);
    const [committedSearch, setCommittedSearch] = useState("");
    const [searchNonce, setSearchNonce] = useState(0);
    const [viewerRole, setViewerRole] = useState(false);
    const [editLoadingId, setEditLoadingId] = useState<string | null>(null);
    const refreshButtonRef = useRef<HTMLButtonElement>(null);
    const fetchDataRef = useRef<() => Promise<void>>(async () => {});

    const listFieldsParam = useMemo(
      () => extractTableFieldsFromColumns(cols, ["customerId", "name", "status"]),
      [cols],
    );

    const fetchData = useCallback(async () => {
      void searchNonce;

      if (!customerId) {
        setData({ content: [], page: { totalElements: 0 } });
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const result = normalizePagedSourceSystems(
          (await sourceSystemService.getSourceSystemsByCustomerId(
            customerId,
            currentPage - 1,
            itemsPerPage,
            committedSearch || undefined,
            listFieldsParam,
          )) as PagedResponse,
        );

        setData(result);
      } catch (error) {
        console.error("Failed to load source systems:", error);
        setData({ content: [], page: { totalElements: 0 } });
      } finally {
        setLoading(false);
      }
    }, [customerId, committedSearch, currentPage, itemsPerPage, searchNonce, listFieldsParam]);

    fetchDataRef.current = fetchData;

    const handleEditClick = useCallback(
      async (row: Record<string, unknown>) => {
        if (!handleEdit) {
          return;
        }

        const id = row?.id != null ? String(row.id) : "";
        try {
          if (id) {
            setEditLoadingId(id);
            const fullRecord = await sourceSystemService.getSourceSystem(id);
            await handleEdit(fullRecord as Record<string, unknown>);
            return;
          }
          await handleEdit(row);
        } catch (error) {
          console.error("Failed to load source system for edit:", error);
          await handleEdit(row);
        } finally {
          setEditLoadingId(null);
        }
      },
      [handleEdit],
    );

    useImperativeHandle(ref, () => ({
      refresh() {
        void fetchDataRef.current?.();
      },
    }));

    useEffect(() => {
      getCurrentUserRoles().then((roles) => setViewerRole(isViewer(roles)));
    }, []);

    useEffect(() => {
      setCurrentPage(1);
    }, [customerId]);

    useEffect(() => {
      void fetchData();
    }, [fetchData]);

    const currentItems = data.content ?? [];
    const totalRows = data.page?.totalElements ?? 0;

    const handleCopy = (row: Record<string, unknown>) => {
      navigator.clipboard.writeText(String(row.id ?? ""));
      notifySuccessShort(t("common.rowCopied"), t("common.copied"));
    };

    const renderActions = useCallback(
      (params: GridRenderCellParams) => {
        const row = params.row as Record<string, unknown>;
        const rowId = row?.id != null ? String(row.id) : "";
        const isEditLoading = editLoadingId != null && editLoadingId === rowId;

        return (
          <RowActionButtons
            loading={isEditLoading}
            onEdit={handleEdit ? () => void handleEditClick(row) : undefined}
            onDelete={handleDelete && !viewerRole ? () => void handleDelete(row) : undefined}
            onCopy={() => handleCopy(row)}
            copyValue={String(row.id ?? "")}
          />
        );
      },
      [handleDelete, handleEdit, handleEditClick, editLoadingId, viewerRole],
    );

    const columns: GridColDef[] = useMemo(() => {
      const actionCol: GridColDef = {
        field: "__actions",
        headerName: t("common.actions"),
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        minWidth: 120,
        flex: 1,
        renderCell: renderActions,
      };
      const mapped = rdtLikeToDataGridColumns(cols, "d");
      return [actionCol, ...mapped];
    }, [cols, renderActions, t]);

    const getRowId = useCallback((row: Record<string, unknown>) => {
      const id = row.id ?? row.uuid;
      if (id != null && String(id) !== "") return String(id);
      return `row_${JSON.stringify(row).slice(0, 48)}`;
    }, []);

    const handleSearchApply = useCallback((next: string) => {
      setCommittedSearch(next);
      setCurrentPage(1);
      setSearchNonce((n) => n + 1);
    }, []);

    return (
      <Paper variant="outlined" sx={fillDataTableRootSx}>
        <Box sx={{ flexShrink: 0, width: "100%" }}>
          <FilterPanel
            embedded
            defaultOpen
            activeCount={committedSearch.trim() ? 1 : 0}
            onClear={committedSearch.trim() ? () => handleSearchApply("") : undefined}
            actions={
              <>
                <IconButton
                  ref={refreshButtonRef}
                  size="small"
                  onClick={() => void fetchData()}
                  color="primary"
                  aria-label={t("common.refreshTable")}
                  sx={{ width: 30, height: 30 }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
                {headerActions}
              </>
            }
          >
            <FilterSearchField
              value={committedSearch}
              onApply={handleSearchApply}
              placeholder="name"
              disabled={loading}
            />
          </FilterPanel>
        </Box>

        <Box sx={fillDataTableGridWrapSx}>
          <DataGrid
            rows={Array.isArray(currentItems) ? currentItems : []}
            columns={columns}
            getRowId={getRowId}
            loading={loading}
            paginationMode="server"
            rowCount={totalRows}
            paginationModel={{
              page: Math.max(0, currentPage - 1),
              pageSize: itemsPerPage,
            }}
            onPaginationModelChange={(model) => {
              setCurrentPage(model.page + 1);
              setItemsPerPage(model.pageSize);
            }}
            pageSizeOptions={[50, 100, 200, 500]}
            sortingOrder={["desc", "asc"]}
            disableRowSelectionOnClick
            localeText={{ noRowsLabel: t("common.noRecords") }}
            sx={{
              ...fillDataGridSx,
              "& .MuiDataGrid-cell": { alignItems: "flex-start", py: 1 },
            }}
          />
        </Box>
      </Paper>
    );
  },
);

ProfileSourceSystemsTable.displayName = "ProfileSourceSystemsTable";

export default ProfileSourceSystemsTable;
